// src/context/ThemeContext.jsx
//
// Theme state for QDex. Persists the user's choice ('light' | 'dark' | 'system')
// to localStorage and reflects the *resolved* theme onto <html data-theme="...">,
// which drives the CSS variables defined in css/theme.css.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const ThemeContext = createContext({
    preference: 'system',
    resolvedTheme: 'light',
    setPreference: () => {},
    toggleTheme: () => {},
});

const STORAGE_KEY = 'qdex-theme';
const VALID = ['light', 'dark', 'system'];

const getSystemTheme = () =>
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const readStoredPreference = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return VALID.includes(stored) ? stored : 'system';
    } catch {
        return 'system';
    }
};

export const ThemeProvider = ({ children }) => {
    const [preference, setPreferenceState] = useState(readStoredPreference);
    const [systemTheme, setSystemTheme] = useState(getSystemTheme);

    // Track the OS preference so 'system' mode stays in sync live.
    useEffect(() => {
        if (!window.matchMedia) return undefined;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => setSystemTheme(e.matches ? 'dark' : 'light');
        mq.addEventListener?.('change', handler);
        return () => mq.removeEventListener?.('change', handler);
    }, []);

    const resolvedTheme = preference === 'system' ? systemTheme : preference;

    // Apply the resolved theme to the document root.
    useEffect(() => {
        document.documentElement.dataset.theme = resolvedTheme;
    }, [resolvedTheme]);

    const setPreference = useCallback((next) => {
        const value = VALID.includes(next) ? next : 'system';
        setPreferenceState(value);
        try {
            localStorage.setItem(STORAGE_KEY, value);
        } catch {
            /* ignore persistence failures (private mode, etc.) */
        }
    }, []);

    // Quick toggle: flips the *resolved* theme to its opposite (explicit choice).
    const toggleTheme = useCallback(() => {
        setPreference(resolvedTheme === 'dark' ? 'light' : 'dark');
    }, [resolvedTheme, setPreference]);

    const value = useMemo(
        () => ({ preference, resolvedTheme, setPreference, toggleTheme }),
        [preference, resolvedTheme, setPreference, toggleTheme]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
